import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { AppDispatch, RootState } from '../../src/redux/store';
import {
  startOnboarding,
  answerQuestion,
  selectOnboardingAnswers,
  selectOnboardingVariant,
  selectOnboardingStartedAt,
  selectOnboardingPhase,
  selectOnboardingCompleted,
} from '../../src/redux/onboardingSlice';
import { pairFor, pickVariant, newSeed, reactionKey, questionKey, type Question, type Winner } from '../../src/services/onboardingQuiz';
import { OnboardingFrame, ONBOARDING_DESKTOP_MIN } from '../../src/components/onboarding/OnboardingFrame';
import { DuChefBubble } from '../../src/components/onboarding/DuChefBubble';
import { DuelCard, type DuelCardState } from '../../src/components/onboarding/DuelCard';
import { ProgressPile } from '../../src/components/onboarding/ProgressPile';
import { Paprika } from '../../src/styles/theme';
import { trackEvent } from '../../src/services/analyticsService';

/** The design's SQuiz timers: win/lose for OUT_MS, then the next pair is dealt during IN_MS. */
const OUT_MS = 1500;
const IN_MS = 560;

type Phase = 'idle' | 'out' | 'in';

/**
 * Route /onboarding — three this-or-that duels. No back arrow: browser back
 * from the safety screen lands on question 3 with its pick shown, and
 * answering again overwrites it.
 */
export default function OnboardingQuiz() {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { width } = useWindowDimensions();
  const desktop = width >= ONBOARDING_DESKTOP_MIN;
  // The quiz stays mounted under /onboarding/safety and /onboarding/direction.
  // Only the focused screen may redirect, or this one sends a cook who chose
  // « des idées » to the week screen the moment onboarding completes.
  const focused = useIsFocused();

  const completed = useSelector(selectOnboardingCompleted);
  const phase = useSelector(selectOnboardingPhase);
  const answers = useSelector(selectOnboardingAnswers);
  const variant = useSelector(selectOnboardingVariant);
  const startedAt = useSelector(selectOnboardingStartedAt);
  const uid = useSelector((s: RootState) => s.auth?.user?.uid ?? null);

  const [question, setQuestion] = useState<Question>(() => (Math.min(answers.length, 2) + 1) as Question);
  const [animPhase, setAnimPhase] = useState<Phase>('idle');
  const [picked, setPicked] = useState<Winner | null>(null);
  const [line, setLine] = useState<string | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // Start once; a reload never re-rolls the variant.
  useEffect(() => {
    if (!startedAt) {
      const seed = uid ?? newSeed();
      dispatch(startOnboarding({ seed, variant: pickVariant(seed), startedAt: new Date().toISOString() }));
    }
  }, [startedAt, uid, dispatch]);

  // Rehydration lands after the first render: follow the persisted answers
  // whenever no animation is running (reload mid-quiz, browser back).
  useEffect(() => {
    if (animPhase !== 'idle' || picked !== null) return;
    const target = (Math.min(answers.length, 2) + 1) as Question;
    setQuestion((q) => (q === target ? q : target));
  }, [answers.length, animPhase, picked]);

  // Resume where the cook left off; completed cooks never see the quiz.
  useEffect(() => {
    if (!focused) return;
    if (completed) router.replace('/(app)');
    else if (phase === 'safety' && answers.length === 3 && animPhase === 'idle' && question === 3 && picked === null) {
      // Browser back from safety: stay here on question 3, showing the pick.
    } else if (phase === 'profile') router.replace('/onboarding/profile');
    else if (phase === 'direction') router.replace('/onboarding/direction');
  }, [focused, completed, phase, answers.length, animPhase, question, picked, router]);

  const pair = useMemo(() => pairFor(question, answers, variant), [question, answers, variant]);
  const lang: 'fr' | 'en' = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const shownPick: Winner | null = picked ?? (answers.length >= question ? answers[question - 1] : null);

  const onPick = useCallback((winner: Winner) => {
    if (animPhase !== 'idle') return; // tap guard during the animation
    setPicked(winner);
    setAnimPhase('out');
    timers.current.push(setTimeout(() => {
      dispatch(answerQuestion({
        question,
        pairId: pair.pairId,
        cardA: pair.a.recipeId,
        cardB: pair.b.recipeId,
        winner,
      }));
      setLine(t(reactionKey(question, winner)));
      if (question === 3) {
        router.push('/onboarding/safety');
        return;
      }
      setQuestion((question + 1) as Question);
      setPicked(null);
      setAnimPhase('in');
      timers.current.push(setTimeout(() => setAnimPhase('idle'), IN_MS));
    }, OUT_MS));
  }, [animPhase, dispatch, pair, question, router, t]);

  const cardState = (side: Winner): DuelCardState => {
    if (animPhase === 'out') return picked === side ? 'win' : 'lose';
    if (animPhase === 'in') return 'arrive';
    if (shownPick === side && answers.length >= question) return 'chosen';
    return 'idle';
  };

  const bubble = (
    <DuChefBubble tone={line ? 'hot' : 'say'} testID="onb-bubble">
      {line ?? t('onboarding.quiz.hello')}
    </DuChefBubble>
  );
  const title = <Text style={styles.question}>{t(questionKey(question))}</Text>;
  const axisLabel = <Text style={styles.axis}>{t(`onboarding.quiz.axis.${pair.axis}`)}</Text>;

  const cards = (
    <View style={[styles.cards, desktop ? styles.cardsSide : styles.cardsStack]}>
      <DuelCard entry={pair.a} state={cardState('a')} onPress={() => onPick('a')} testID="onb-duel-a" lang={lang} layout={desktop ? 'side' : 'stack'} index={0} axis={pair.axis} />
      <DuelCard entry={pair.b} state={cardState('b')} onPress={() => onPick('b')} testID="onb-duel-b" lang={lang} layout={desktop ? 'side' : 'stack'} index={1} axis={pair.axis} />
    </View>
  );
  const progress = <ProgressPile question={question} answered={Math.min(answers.length, question - 1 + (animPhase === 'out' ? 1 : 0))} />;

  // A cook who already has an account is not here to be profiled again (#741).
  // /auth sends them to the app once they are in, and its own « Continuer sans
  // compte » brings a visitor who changed their mind back to this quiz.
  const signIn = uid ? null : (
    <Pressable
      onPress={() => {
        trackEvent('guest_sign_in_clicked', { source: 'onboarding_quiz', question });
        router.push('/auth');
      }}
      hitSlop={10}
      accessibilityRole="link"
      testID="onb-sign-in"
    >
      <Text style={styles.signIn}>{t('onboarding.quiz.haveAccount')}</Text>
    </Pressable>
  );

  return (
    <OnboardingFrame
      kicker={t('onboarding.quiz.kicker')}
      brandTrailing={signIn}
      rail={desktop ? <>{title}{bubble}</> : <>{bubble}{axisLabel}{title}</>}
      footer={progress}
    >
      {desktop ? <>{progress}{cards}</> : cards}
    </OnboardingFrame>
  );
}

const styles = StyleSheet.create({
  question: { fontFamily: 'Newsreader-Regular', fontSize: 23, lineHeight: 28, color: Paprika.textPrimary },
  axis: { fontFamily: 'Inter-SemiBold', fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', color: Paprika.accent },
  signIn: { fontFamily: 'Inter-Medium', fontSize: 13, lineHeight: 18, color: Paprika.accent, textDecorationLine: 'underline' },
  cards: { flex: 1, gap: 16, minHeight: 0 },
  cardsStack: { flexDirection: 'column' },
  cardsSide: { flexDirection: 'row' },
});
